/**
 * P0 Terms of Use Page
 */
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Breadcrumbs,
} from '@mui/material';
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">Terms of Use</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Terms of Use
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: January 2025
        </Typography>

        <Box sx={{ '& > section': { mb: 4 } }}>
          <section>
            <Typography variant="h6" gutterBottom>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing and using MindWell ("the Service"), you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the Service.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              2. Description of Service
            </Typography>
            <Typography variant="body1" paragraph>
              MindWell is a practice management platform designed for mental health professionals, including psychiatrists, psychologists, and clinics. The Service provides tools for:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Appointment scheduling and management</li>
              <li>Patient record management</li>
              <li>Prescription creation and management</li>
              <li>Clinic operations and staff management</li>
              <li>Communication and notifications</li>
            </Box>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              3. User Accounts
            </Typography>
            <Typography variant="body1" paragraph>
              To use the Service, you must:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Provide accurate and complete information during registration</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activities under your account</li>
            </Box>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              4. Data Location and Storage
            </Typography>
            <Typography variant="body1" paragraph>
              Your data is stored securely on our servers. We use industry-standard security measures to protect your information. Data is stored in compliance with applicable data protection laws.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              5. Backup and Data Access
            </Typography>
            <Typography variant="body1" paragraph>
              We maintain automated daily backups of your data with a retention period of 7-30 days. You can request access to your data at any time by contacting support@mindwell.com.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              6. Acceptable Use
            </Typography>
            <Typography variant="body1" paragraph>
              You agree not to:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any laws in your jurisdiction</li>
              <li>Transmit any viruses or malicious code</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the Service</li>
            </Box>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              7. Medical Disclaimer
            </Typography>
            <Typography variant="body1" paragraph>
              MindWell is a practice management tool and does not provide medical advice, diagnosis, or treatment. All medical decisions and treatments are the sole responsibility of licensed healthcare professionals using the Service.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              8. Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph>
              The Service and its original content, features, and functionality are owned by MindWell and are protected by international copyright, trademark, and other intellectual property laws.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              9. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              MindWell shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              10. Changes to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to modify these terms at any time. We will notify users of significant changes via email or through the Service. Continued use after changes constitutes acceptance.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              11. Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We may terminate or suspend your account immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              12. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              For questions about these Terms, please contact us at:
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> support@mindwell.com
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>Support:</strong> Available through the in-app support system
            </Typography>
          </section>
        </Box>
      </Paper>
    </Container>
  );
};

export default Terms;

